import React from 'react';
import PropTypes from 'prop-types';
import { Promise } from 'es6-promise';
import TextField from 'material-ui/TextField';
import { getAssetTypeByName, updateAssetDraft, uploadAsset } from '../../actions/AssetActionCreator';
import SelectAssetType from './SelectAssetType';
import Toggle from 'material-ui/Toggle';
import FileUpload, { validateMimeType } from '../Common/FileUpload';
import Checkbox from 'material-ui/Checkbox';
import AssetWysiwygEditor from './AssetWysiwygEditor';
import AssetStore from '../../stores/AssetStore';

export const validate = ({id, name, typeId, asset, type}) => {
    const errors = {};
    if (!name) {
        errors.name = 'Required field';
    }
    if (!typeId) {
        errors.type = 'Required field';
    }
    if (!!type && type.asAttachment) {
        if (!asset) {
            if (!id) {
                errors.asset = 'Required';
            }
        } else if (!validateMimeType(asset.type, type.allowedMimeTypes)) {
            errors.asset = 'Invalid file type';
        }
    }
    return errors;
};

const uploadEmbeddedImage = (file) => new Promise(async (resolve, reject) => {
    const type = await getAssetTypeByName('image');
    const draft = {
        asset: file,
        name: `${file.fileName}_${Date.now()}`,
        typeId: type.id,
        type,
        content: ''
    };

    uploadAsset(draft).then(
        (assetId) => {
            const image = AssetStore.getById(assetId);
            if (!image) { reject({error: true, message: 'Upload failed', data: null}); }
            resolve({data: {link: image.link}});
        },
        (error) => reject({error: true, message: error, data: null})
    );
});

const AsText = ({draft, onUpdate, allowWysiwyg = true, errors}) => (
    <div className="cent-col-1">
        {(!!draft.rawHTML || !allowWysiwyg) ? (
            <TextField
                key="content"
                name="content"
                value={draft.content}
                floatingLabelText="Content"
                onChange={({target}, value) => onUpdate({[target.name]: value})}
                multiLine={true}
                fullWidth={true}
                rowsMax={40}
            />
        ) : (
            <AssetWysiwygEditor
                value={draft.content}
                onChange={(value) => onUpdate({content: value})}
                editorProps={{
                    key:         'content',
                    name:        'content',
                    placeholder: 'Content'
                }}
                uploadCallback={uploadEmbeddedImage}
            />
        )}
        {!!allowWysiwyg && (
            <Toggle
                label="Edit raw HTML"
                labelPosition="right"
                toggled={!!draft.rawHTML}
                onToggle={() => onUpdate({rawHTML: !draft.rawHTML})}
            />
        )}
    </div>
);

const AsAttachment = ({draft, onUpdate, errors}) => (
    <div className="cent-col-1">
        {(!draft.id || (!!draft.type && !!draft.type.versionable)) && (
            <FileUpload
                key="asset"
                name="asset"
                value={draft.asset}
                onChange={(name, file) => onUpdate({[name]: file})}
                allowedMimeTypes={draft.type.allowedMimeTypes}
                errorText={errors.asset}
                required={!draft.id}
            />
        )}
    </div>
);

const Versionable = ({draft, onUpdate, errors}) => (
    <div className="cent-col-1">
        <div className='cent-checkbox-wrapper'>
            <Checkbox
                label="Archive previous version"
                checked={!!draft.copy}
                onCheck={() => onUpdate({copy: !draft.copy})}
            />
        </div>
            {!!draft.copy && (
                <TextField
                    floatingLabelText="Version name"
                    value={draft.versionName}
                    onChange={({target}, value) => onUpdate({versionName: value})}
                    fullWidth={true}
                />
            )}
    </div>
);

const AssetForm = ({draft, errors, allowWysiwyg = true, onUpdate, ...props}) => {
    const isEdit = !!Number(draft.id);
    const asText = !!draft.type && !draft.type.asAttachment;
    const asAttachment = !!draft.type && !!draft.type.asAttachment;
    const isVersionable = isEdit && !!draft.type && !!draft.type.versionable;

    return (
        <div className='cent-flex-row'>
            <div className="cent-col-1">
                <TextField
                    name="name"
                    floatingLabelText="Name"
                    value={draft.name}
                    errorText={errors.name}
                    fullWidth={true}
                    onChange={({target}, value) => onUpdate({[target.name]: value})}
                />
            </div>
            {!isEdit && (
                <div className="cent-col-1">
                    <SelectAssetType
                        value={draft.typeId}
                        errorText={errors.type}
                        onSelect={(id, name, type) => onUpdate({typeId: type.id, type})}
                        allowedTypes={props.allowedTypes}
                    />
                </div>
            )}
            {asText && <AsText draft={draft} errors={errors} onUpdate={onUpdate} allowWysiwyg={allowWysiwyg} /> }
            {asAttachment && <AsAttachment draft={draft} errors={errors} onUpdate={onUpdate} /> }
            {isVersionable && <Versionable draft={draft} errors={errors} onUpdate={onUpdate} /> }
        </div>
    );

};

AssetForm.propTypes = {
    draft: PropTypes.shape({
        name: PropTypes.string,
        typeId: PropTypes.number,
        asAttachment: PropTypes.bool,
    }),
    allowedTypes: PropTypes.array,
    allowWysiwyg: PropTypes.bool,
    onUpdate: PropTypes.func,
};

AssetForm.defaultProps = {
    onUpdate: updateAssetDraft,
    allowWysiwyg: true,
};

export default AssetForm;
